import {
  BannersWithCtaLayoutApiModel,
  CreateCurrentlyOnTvItemRequestApiModel,
  CreateLocalizedPlatformImageRequestApiModel,
  CreateLocalizedStringApiModel,
  CreateMarketingPageItemButtonApiModel,
  CreatePlatformImageRequestApiModel,
  CreateTvChannelLogoItemRequestApiModel,
  GenericLayoutApiModel,
  LocalizedStringApiModel,
  MarketingPageItemButtonActionApiModel,
  UpdateCurrentlyOnTvItemRequestApiModel,
  UpdateTvChannelLogoItemRequestApiModel,
  MarketingVideoSourceApiModel,
} from '@cxb/editor-stb-api';
import { TraySelectorType } from '../components/tray-selector/tray-selector.enum';
import { ChannelDomainModel } from '../../domain/currently-on-tv-tray';
import {
  TrayWithBackgroundImages,
  TrayWithCountryTargeting,
  TrayWithDisplayServiceTypeIcon,
  TrayWithFullTargeting,
  TrayWithLayout,
  TrayWithLocalizedBackgroundImages,
  TrayWithLocalizedDescription,
  TrayWithLocalizedHeadlineText,
  TrayWithLocalizedTitle,
  TrayWithName,
  TrayWithPlatformTargeting,
  TrayWithPrimaryButton,
  TrayWithScheduling,
  TrayWithUserTargeting,
  TrayWithVideoSource,
  TrayWithVideoSourceApiModel,
  TrayWithVideoUrl,
} from '../../domain/tray-common';
import { MarketingItemLayout } from '../../domain/domain-layouts';
import { ImageData, LocalizedDomainImages } from '../../domain/localized-domain-images';
import { MarketingTrayButton } from '../../domain/marketing-tray-button';
import { MarketingButtonAction } from '../../shared/enums/marketing.enums';
import { MarketingVideoSource } from '../components/marketing-video-tray-form/marketing-video-sources';

const mapDomainButtonActionToApi = (action: MarketingButtonAction): MarketingPageItemButtonActionApiModel => {
  switch (action) {
    case MarketingButtonAction.SIGN_UP:
      return MarketingPageItemButtonActionApiModel.SignUp;
    case MarketingButtonAction.SIGN_IN:
      return MarketingPageItemButtonActionApiModel.LogIn;
    case MarketingButtonAction.INTERNAL_PAGE:
      return MarketingPageItemButtonActionApiModel.CxbPage;
    case MarketingButtonAction.CHOOSE_NEW_PASSWORD:
      return MarketingPageItemButtonActionApiModel.NewPassword;
    case MarketingButtonAction.PROGRAM:
      return MarketingPageItemButtonActionApiModel.Content;
    case MarketingButtonAction.EXTERNAL_URL:
      return MarketingPageItemButtonActionApiModel.ExternalUrl;
    case MarketingButtonAction.RENEW_OR_COMPLETE_SUBSCRIPTION:
      return MarketingPageItemButtonActionApiModel.CompleteSubscription;
    default:
      return undefined;
  }
};

const numericDateToStringDate = (numericDate: number): string | undefined => {
  return numericDate ? new Date(numericDate).toISOString() : undefined;
};

export const createLocalizedText = (en: string, ar: string): { en: string; ar: string } => {
  return {
    en: en || '',
    ar: ar || '',
  };
};

export const mapDomainButtonToApi = (button: MarketingTrayButton): CreateMarketingPageItemButtonApiModel => {
  return {
    labelLocalized: {
      ar: button.buttonTextArabic,
      en: button.buttonTextEnglish,
    },
    action: mapDomainButtonActionToApi(button.action),
    targetPageId: button.buttonActionPageTargetId,
    mpxProgramId: button.buttonActionProgramTarget?.id.toString(),
    externalUrl: button.buttonActionExternalUrl,
  };
};

export const mapDomainImageDataToApi = (imageData: ImageData): CreatePlatformImageRequestApiModel => {
  return {
    imageId: imageData.id,
    platformId: imageData.platformId || '',
  };
};

export const mapDomainCtaLayoutToApi = (template: MarketingItemLayout): GenericLayoutApiModel => {
  switch (template) {
    case MarketingItemLayout.CTA_LEFT:
      return GenericLayoutApiModel.Left;
    case MarketingItemLayout.CTA_RIGHT:
      return GenericLayoutApiModel.Right;
    default:
      return undefined;
  }
};

export const nameLocalizedMapperDomainToApi = <T extends TrayWithLocalizedTitle>(tray: T): { nameLocalized: LocalizedStringApiModel } => {
  return {
    nameLocalized: createLocalizedText(tray.titleEnglish, tray.titleArabic),
  };
};

export const userTargetingMapper = <T extends TrayWithUserTargeting>(tray: T): { userSegmentIds: string[] } => {
  return {
    userSegmentIds: tray.userSegmentIds,
  };
};

export const platformTargetingMapper = <T extends TrayWithPlatformTargeting>(tray: T): { platformIds: string[] } => {
  return {
    platformIds: tray.platformIds,
  };
};

export const countryTargetingMapper = <T extends TrayWithCountryTargeting>(
  tray: T
): { countryIds: string[]; countryGroupIds: string[] } => {
  return {
    countryIds: tray.countryIds,
    countryGroupIds: [],
  };
};

export const targetingMapperDomainToApi = <T extends TrayWithFullTargeting>(
  tray: T
): { userSegmentIds: string[]; countryIds: string[]; countryGroupIds: string[] } => {
  return {
    ...userTargetingMapper(tray),
    ...countryTargetingMapper(tray),
  };
};
export const nameMapper = <T extends TrayWithName>(tray: T): { name: string } => {
  return {
    name: tray.name,
  };
};

export const videoURLMapper = <T extends TrayWithVideoUrl>(tray: T): { videoUrl: string } => {
  return {
    videoUrl: tray.videoUrl,
  };
};

export const videoSourceMapperDomainToApi = <T extends TrayWithVideoSource>(tray: T): { videoSource: MarketingVideoSourceApiModel } => {
  const mapVideoSourceToApi = (videoSource: MarketingVideoSource) => {
    // eslint-disable-next-line sonarjs/no-all-duplicated-branches
    switch (videoSource) {
      case MarketingVideoSource.YouTube:
        return MarketingVideoSourceApiModel.Youtube;
      default:
        return MarketingVideoSourceApiModel.Youtube;
    }
  };

  return {
    videoSource: mapVideoSourceToApi(tray.videoSource),
  };
};

export const videoSourceMapperApiToDomain = <T extends TrayWithVideoSourceApiModel>(tray: T): { videoSource: MarketingVideoSource } => {
  const mapVideoSourceToApi = (videoSource: MarketingVideoSourceApiModel) => {
    // eslint-disable-next-line sonarjs/no-all-duplicated-branches
    switch (videoSource) {
      case MarketingVideoSourceApiModel.Youtube:
        return MarketingVideoSource.YouTube;
      default:
        return MarketingVideoSource.YouTube;
    }
  };

  return {
    videoSource: mapVideoSourceToApi(tray.videoSource),
  };
};

export const layoutMapperDomainToApi = <T extends TrayWithLayout>(
  tray: T
): { layout: GenericLayoutApiModel | BannersWithCtaLayoutApiModel } => {
  return {
    layout: mapDomainLayoutToApi(tray.layout),
  };
};

export const scheduledDateMapperDomainToApi = <T extends TrayWithScheduling>(tray: T): { scheduledFrom?: string; scheduledTo?: string } => {
  return {
    scheduledFrom: numericDateToStringDate(tray.scheduledFrom),
    scheduledTo: numericDateToStringDate(tray.scheduledTo),
  };
};

export const displayServiceTypeIconMapperDomainToApi = <T extends TrayWithDisplayServiceTypeIcon>(
  tray: T
): { displayServiceTypeIcon: boolean } => {
  return {
    displayServiceTypeIcon: tray.displayServiceTypeIcon,
  };
};

export const titleLocalizedFromHeadlineMapper = <T extends TrayWithLocalizedHeadlineText>(
  tray: T
): { titleLocalized: CreateLocalizedStringApiModel } => {
  return {
    titleLocalized: createLocalizedText(tray.headlineTextEnglish, tray.headlineTextArabic),
  };
};

export const buttonMapperDomainToApi = <T extends TrayWithPrimaryButton>(tray: T): { button: CreateMarketingPageItemButtonApiModel } => {
  return {
    button: mapDomainButtonToApi(tray.primaryButton),
  };
};

export const localizedBackgroundImagesMapper = <T extends TrayWithLocalizedBackgroundImages>(
  tray: T
): { backgroundImages: CreateLocalizedPlatformImageRequestApiModel } => {
  return {
    backgroundImages: {
      en: tray.backgroundImages.en.map(mapDomainImageDataToApi),
      ar: tray.backgroundImages.ar.map(mapDomainImageDataToApi),
    },
  };
};

export const backgroundImagesMapperDomainToApi = <T extends TrayWithBackgroundImages>(
  tray: T
): { backgroundImages: CreatePlatformImageRequestApiModel[] } => {
  return {
    backgroundImages: tray.backgroundImages.map(mapDomainImageDataToApi),
  };
};

export const descriptionLocalizedMapperDomainToApi = <T extends TrayWithLocalizedDescription>(
  tray: T
): { descriptionLocalized: CreateLocalizedStringApiModel } => {
  return {
    descriptionLocalized: createLocalizedText(tray.descriptionTextEnglish, tray.descriptionTextArabic),
  };
};

export const mapChannelDomainModelToApiModel = (
  channel: ChannelDomainModel
): CreateTvChannelLogoItemRequestApiModel &
  UpdateTvChannelLogoItemRequestApiModel &
  CreateCurrentlyOnTvItemRequestApiModel &
  UpdateCurrentlyOnTvItemRequestApiModel => {
  return {
    id: channel.id,
    order: channel.order,
    channelId: channel.channel.id,
  };
};

// You can use it for every tray
const mapDomainLayoutToApi = (template: TraySelectorType): GenericLayoutApiModel | BannersWithCtaLayoutApiModel => {
  switch (template) {
    case TraySelectorType.LandscapeTwoCardsTray:
      return GenericLayoutApiModel.TwoCards;
    case TraySelectorType.LandscapeThreeCardsTray:
      return GenericLayoutApiModel.ThreeCards;
    case TraySelectorType.LandscapeFourCardsTray:
      return GenericLayoutApiModel.FourCards;
    case TraySelectorType.MarketingCTAThreeCard:
      return BannersWithCtaLayoutApiModel.ThreeThumbnails;
    case TraySelectorType.MarketingCTATwoCard:
      return BannersWithCtaLayoutApiModel.TwoThumbnails;
    case TraySelectorType.PortraitLarge:
      return GenericLayoutApiModel.Portrait;
    default:
      return undefined;
  }
};

export const mapLocalizedDomainImagesToCreateLocalizedPlatformImageRequestApiModelArray = (
  images: LocalizedDomainImages
): CreateLocalizedPlatformImageRequestApiModel => {
  return {
    en: images.en.map((i) => ({
      imageId: i.id,
      platformId: i.platformId,
      //locale: 'en',
    })),
    ar: images.ar.map((i) => ({
      imageId: i.id,
      platformId: i.platformId,
      //locale: 'ar',
    })),
  };
};
